import { useState, useEffect } from "react";
import API from "../lib/api";
import { fmtDate } from "../lib/dateUtil";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { MagicWand, ArrowsClockwise, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

import DateInput from "./DateInput";
/**
 * Create a new In-House PO. Captures the high-level header (operative, period,
 * rate labels + defaults, mileage rate, optional VAT). The line items are added
 * inside the detail page after creation, where the table can render properly.
 */
export default function CreateInHousePoDialog({ open, onClose, onCreated }) {
  const [team, setTeam] = useState([]);
  const [form, setForm] = useState({
    operative_id: "",
    title: "",
    period_start: "",
    period_end: "",
    rate_1_label: "Day rate",
    rate_2_label: "Overtime rate",
    rate_1_default: 180,
    rate_2_default: 0,
    rate_2_enabled: false,
    vat_enabled: false,
    vat_rate: 20,
    notes: "",
  });
  const [creating, setCreating] = useState(false);

  // ── Auto-fill from schedule ──────────────────────────────────────
  const [preview, setPreview] = useState(null);  // { lines, total_jobs, days_with_work }
  const [previewing, setPreviewing] = useState(false);
  const [useSchedule, setUseSchedule] = useState(true);
  const canPreview = form.operative_id && form.period_start && form.period_end;

  // Refresh preview whenever operative + period are set
  useEffect(() => {
    if (!canPreview) {
      setPreview(null);
      return;
    }
    let cancelled = false;
    setPreviewing(true);
    API.post("/in-house/pos/preview-from-schedule", {
      operative_id: form.operative_id,
      period_start: form.period_start,
      period_end: form.period_end,
      rate_1_default: form.rate_1_default,
      rate_2_default: form.rate_2_enabled ? form.rate_2_default : 0,
    }).then(({ data }) => {
      if (!cancelled) setPreview(data);
    }).catch(() => { if (!cancelled) setPreview(null); })
      .finally(() => { if (!cancelled) setPreviewing(false); });
    return () => { cancelled = true; };
  }, [form.operative_id, form.period_start, form.period_end, form.rate_1_default, form.rate_2_default, form.rate_2_enabled, canPreview]);

  useEffect(() => {
    if (!open) return;
    API.get("/in-house/team").then(({ data }) => setTeam(data.team || [])).catch(() => {});
    // Reset preview when reopening
    setPreview(null);
    setUseSchedule(true);
  }, [open]);

  // When operative changes, pre-fill rate fields from their saved profile (if any)
  useEffect(() => {
    if (!form.operative_id || !team.length) return;
    const op = team.find(t => t._id === form.operative_id);
    const rp = op?.rate_profile;
    if (!rp) return;
    setForm(f => ({
      ...f,
      rate_1_label: rp.day_rate_label || f.rate_1_label,
      rate_2_label: rp.helper_rate_label || f.rate_2_label,
      rate_1_default: rp.day_rate ?? f.rate_1_default,
      rate_2_default: rp.helper_rate ?? f.rate_2_default,
      rate_2_enabled: !!rp.helper_rate_enabled,
    }));
  }, [form.operative_id, team]);

  const handleCreate = async () => {
    if (!form.operative_id) {
      toast.error("Select an operative");
      return;
    }
    setCreating(true);
    try {
      const lines = (useSchedule && preview?.lines?.length) ? preview.lines : [];
      const { data } = await API.post("/in-house/pos", { ...form, lines });
      const msg = lines.length
        ? `Created ${data.po_number} with ${lines.length} line${lines.length === 1 ? "" : "s"} from the schedule (jobs, expenses and mileage) — check Units + Cost before sending.`
        : `Created ${data.po_number}`;
      toast.success(msg);
      onCreated && onCreated(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create PO");
    } finally { setCreating(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="create-in-house-po-dialog">
        <DialogHeader>
          <DialogTitle className="font-[Chivo]">New In-House Purchase Order</DialogTitle>
          <DialogDescription>
            Pick the operative and the rate defaults. Line items can be added on the next screen.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label className="text-xs">Operative</Label>
            <Select value={form.operative_id} onValueChange={(v) => setForm(f => ({ ...f, operative_id: v }))}>
              <SelectTrigger data-testid="create-po-operative-select"><SelectValue placeholder="Pick a team member" /></SelectTrigger>
              <SelectContent>
                {team.map(t => (
                  <SelectItem key={t._id} value={t._id} data-testid={`create-po-operative-${t._id}`}>
                    {t.name} <span className="text-muted-foreground text-xs ml-1">({t.email})</span>
                    {t.rate_profile?.day_rate > 0 && <span className="text-[10px] ml-1 text-blue-600">· £{t.rate_profile.day_rate}/day saved</span>}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {form.operative_id && team.find(t => t._id === form.operative_id)?.rate_profile?.day_rate > 0 && (
              <p className="text-[11px] text-blue-600 mt-1 flex items-center gap-1" data-testid="create-po-profile-loaded">
                <CheckCircle size={11} weight="fill" /> Rates pre-filled from this operative&apos;s saved profile — edit below if needed.
              </p>
            )}
          </div>

          <div>
            <Label className="text-xs">Title</Label>
            <Input
              value={form.title}
              onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))}
              placeholder="e.g. Week ending 23rd February"
              data-testid="create-po-title"
            />
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Period start</Label>
              <DateInput

                value={form.period_start}
                onChange={(e) => setForm(f => ({ ...f, period_start: e.target.value }))}
                data-testid="create-po-period-start"
              />
            </div>
            <div>
              <Label className="text-xs">Period end</Label>
              <DateInput

                value={form.period_end}
                onChange={(e) => setForm(f => ({ ...f, period_end: e.target.value }))}
                data-testid="create-po-period-end"
              />
            </div>
          </div>

          {/* Quick period presets */}
          <div className="flex flex-wrap gap-1.5">
            {[
              { label: "This week", offset: 0 },
              { label: "Last week", offset: -7 },
              { label: "This fortnight", offset: 0, fortnight: true },
            ].map(preset => (
              <button
                key={preset.label}
                type="button"
                className="text-[11px] px-2 py-1 rounded-full border border-border bg-muted/40 hover:bg-blue-50 hover:border-blue-300 dark:hover:bg-blue-900/30 transition"
                onClick={() => {
                  const today = new Date();
                  const dow = today.getDay(); // 0 = Sun
                  const mondayDelta = ((dow + 6) % 7); // days back to Monday
                  const start = new Date(today);
                  start.setDate(today.getDate() - mondayDelta + preset.offset);
                  const end = new Date(start);
                  end.setDate(start.getDate() + (preset.fortnight ? 13 : 6));
                  const iso = (d) => d.toISOString().slice(0, 10);
                  setForm(f => ({ ...f, period_start: iso(start), period_end: iso(end) }));
                }}
                data-testid={`create-po-preset-${preset.label.replace(/\s/g, "-").toLowerCase()}`}
              >
                {preset.label}
              </button>
            ))}
          </div>

          {/* Auto-fill from schedule preview */}
          {canPreview && (
            <div className={`border rounded-md p-3 transition ${preview?.total_jobs > 0 ? "border-blue-300 bg-blue-50/50 dark:bg-blue-950/20 dark:border-blue-800" : "border-border bg-muted/20"}`} data-testid="create-po-schedule-preview">
              <div className="flex items-start gap-3">
                <MagicWand size={18} weight="duotone" className={preview?.total_jobs > 0 ? "text-blue-600 mt-0.5" : "text-muted-foreground mt-0.5"} />
                <div className="flex-1 min-w-0">
                  {previewing ? (
                    <p className="text-sm flex items-center gap-1.5"><ArrowsClockwise size={14} className="animate-spin" /> Checking the schedule…</p>
                  ) : preview === null ? (
                    <p className="text-sm text-muted-foreground">Couldn&apos;t check the schedule — you can still build the PO manually.</p>
                  ) : preview.total_jobs === 0 ? (
                    <>
                      <p className="text-sm font-medium">No jobs assigned in this period</p>
                      <p className="text-xs text-muted-foreground mt-0.5">The PO will start empty — add days manually after creating.</p>
                    </>
                  ) : (
                    <>
                      <p className="text-sm font-medium flex items-center gap-1.5">
                        <CheckCircle size={14} weight="fill" className="text-blue-600" />
                        Found <strong>{preview.total_jobs} job{preview.total_jobs === 1 ? "" : "s"}</strong> across <strong>{preview.days_with_work} day{preview.days_with_work === 1 ? "" : "s"}</strong>
                        {(preview.expense_lines > 0 || preview.mileage_lines > 0) && (
                          <span className="text-xs text-muted-foreground font-normal" data-testid="create-po-preview-extras">
                            · {preview.expense_lines || 0} expense line{preview.expense_lines === 1 ? "" : "s"} · {preview.mileage_lines || 0} mileage line{preview.mileage_lines === 1 ? "" : "s"}
                          </span>
                        )}
                      </p>
                      <label className="flex items-center gap-2 mt-2 cursor-pointer text-xs">
                        <input
                          type="checkbox"
                          checked={useSchedule}
                          onChange={(e) => setUseSchedule(e.target.checked)}
                          data-testid="create-po-use-schedule"
                        />
                        Pre-fill the PO: one line per job (day rate on the first job of each day only), an expenses line per job with receipts, and a mileage line per day.
                      </label>
                      {useSchedule && (
                        <div className="mt-2 max-h-40 overflow-y-auto space-y-1 text-[11px] text-muted-foreground border-t border-blue-200 dark:border-blue-900/50 pt-2">
                          {preview.lines.map(l => (
                            <div key={l.id} className="flex items-start gap-2 flex-wrap" data-testid={`create-po-preview-line-${l.kind || "job"}`}>
                              <CheckCircle size={11} weight="fill" className={l.kind === "mileage" ? "text-emerald-500 mt-0.5 shrink-0" : l.kind === "expenses" ? "text-amber-500 mt-0.5 shrink-0" : "text-blue-500 mt-0.5 shrink-0"} />
                              <span className="font-mono shrink-0">{fmtDate(l.date)}</span>
                              {l.kind && l.kind !== "job" && <span className={`uppercase text-[9px] font-semibold px-1 rounded ${l.kind === "mileage" ? "bg-emerald-500/10 text-emerald-700" : "bg-amber-500/10 text-amber-700"}`}>{l.kind}</span>}
                              <span className="font-medium text-foreground">{(l.task || "").slice(0, 45)}{(l.task || "").length > 45 ? "…" : ""}</span>
                              {l.kind !== "mileage" && l.kind !== "expenses" && l.address && <span className="opacity-70">· {l.address.slice(0, 40)}{l.address.length > 40 ? "…" : ""}</span>}
                              <span className="shrink-0">
                                {l.kind === "expenses" ? `£${Number(l.cost || 0).toFixed(2)}`
                                  : l.kind === "mileage" ? `${Number(l.units || 0)} mi${Number(l.cost) > 0 ? ` · £${(Number(l.units) * Number(l.cost)).toFixed(2)}` : ""}`
                                  : Number(l.rate_1) > 0 ? `${form.rate_1_label || "Day rate"}: £${Number(l.rate_1).toFixed(0)}` : "no day rate (same day)"}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Day rate name</Label>
              <Input value={form.rate_1_label} onChange={(e) => setForm(f => ({ ...f, rate_1_label: e.target.value }))} placeholder="Day rate" data-testid="create-po-dr1-label" />
            </div>
            <div>
              <Label className="text-xs">Day rate default (£)</Label>
              <Input type="number" step="any" value={form.rate_1_default} onChange={(e) => setForm(f => ({ ...f, rate_1_default: parseFloat(e.target.value) || 0 }))} data-testid="create-po-dr1-default" />
            </div>
          </div>
          <div className="rounded-md border border-border p-3 space-y-2" data-testid="create-po-second-rate-block">
            <div className="flex items-center justify-between gap-3">
              <div>
                <Label className="text-xs">Second rate column</Label>
                <p className="text-[11px] text-muted-foreground">Adds an extra per-line rate (e.g. overtime). Off by default.</p>
              </div>
              <Switch checked={!!form.rate_2_enabled} onCheckedChange={(v) => setForm(f => ({ ...f, rate_2_enabled: v }))} data-testid="create-po-second-rate-toggle" />
            </div>
            {form.rate_2_enabled && (
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Second rate name</Label>
                  <Input value={form.rate_2_label} onChange={(e) => setForm(f => ({ ...f, rate_2_label: e.target.value }))} placeholder="Overtime rate" data-testid="create-po-dr2-label" />
                </div>
                <div>
                  <Label className="text-xs">Default (£)</Label>
                  <Input type="number" step="any" value={form.rate_2_default} onChange={(e) => setForm(f => ({ ...f, rate_2_default: parseFloat(e.target.value) || 0 }))} data-testid="create-po-dr2-default" />
                </div>
              </div>
            )}
          </div>

          <div>
            <Label className="text-xs flex items-center gap-1.5">
              <input
                type="checkbox"
                checked={form.vat_enabled}
                onChange={(e) => setForm(f => ({ ...f, vat_enabled: e.target.checked }))}
                data-testid="create-po-vat-checkbox"
              />
              Add VAT
            </Label>
            <Input
              type="number"
              step="0.1"
              value={form.vat_rate}
              onChange={(e) => setForm(f => ({ ...f, vat_rate: parseFloat(e.target.value) || 0 }))}
              disabled={!form.vat_enabled}
              placeholder="20"
              data-testid="create-po-vat-rate"
              className="mt-1"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleCreate} disabled={creating} data-testid="create-po-submit-btn">
            {creating ? "Creating…" : "Create PO"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
